import { Fragment } from "react";
import { useQuery } from "@tanstack/react-query";
import { BRAND_SLOT_LABEL, lockupSlots } from "@sga/shared";
import { apiGet } from "../lib/api.js";

/**
 * The NNPC / Seplat lockup, as configured in Brand settings — the same logos the applicant sees on
 * a form, so staff chrome and the public form cannot drift apart.
 *
 * Until a superadmin uploads a mark, this falls back to the "SGA" monogram the shell used to
 * hardcode. That fallback is the *only* reason the wordmark still exists; it is not a design
 * choice, and it disappears the moment a logo is set.
 *
 * Sizing is by height alone: both marks are wordmarks around 1.7:1, so `h-8` is ~54px wide each and
 * the full lockup ~125px. Callers place it in boxes narrower than that (the collapsed sidebar is
 * 64px), so every image is additionally allowed to shrink — `max-w-full` with `object-contain`
 * scales the picture down inside a squeezed box instead of letting it paint over its neighbour.
 */

export interface Branding {
  name: string | null;
  logoLayout: string;
  primaryLogoUrl: string | null;
  secondaryLogoUrl: string | null;
  colors: Record<string, string> | null;
}

export const useBranding = () =>
  useQuery({
    queryKey: ["branding"],
    queryFn: () => apiGet<Branding>("/public/branding"),
    // Chrome, not data: refetching it on every window focus would flicker the sidebar for nothing.
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: false,
  });

const Monogram = ({ className }: { className: string }): JSX.Element => (
  <span
    className={`flex shrink-0 items-center justify-center rounded-md bg-primary font-heading font-bold text-primary-foreground ${className}`}
  >
    SGA
  </span>
);

/** The marks the configured layout asks for, in lockup order. */
const marksFor = (branding: Branding): { src: string; alt: string }[] =>
  lockupSlots(branding.logoLayout).flatMap((slot) => {
    const src = slot === "primary" ? branding.primaryLogoUrl : branding.secondaryLogoUrl;
    return src ? [{ src, alt: BRAND_SLOT_LABEL[slot] }] : [];
  });

/**
 * `variant` picks the surface, not the size: on the primary-coloured sign-in panel a mark needs a
 * light plate behind it, because both partner logos are full-colour marks drawn for white.
 *
 * `compact` renders the leading mark only — NNPC, since the swap — for somewhere too narrow for a
 * two-logo lockup.
 */
export const BrandMark = ({
  className = "h-8",
  variant = "light",
  compact = false,
}: {
  className?: string;
  variant?: "light" | "on-primary";
  compact?: boolean;
}): JSX.Element => {
  const { data } = useBranding();

  const marks = data ? marksFor(data) : [];
  if (marks.length === 0) {
    return <Monogram className={`${className} aspect-square text-sm`} />;
  }

  const shown = compact ? marks.slice(0, 1) : marks;
  const plate =
    variant === "on-primary" ? "rounded-md bg-white/95 px-2 py-1" : "rounded-md bg-white";

  return (
    <span className={`inline-flex min-w-0 max-w-full items-center gap-2 ${plate}`}>
      {shown.map((mark, index) => (
        <Fragment key={mark.src}>
          {index > 0 && <span aria-hidden className="h-6 w-px shrink-0 bg-black/10" />}
          <img
            src={mark.src}
            alt={mark.alt}
            className={`${className} w-auto min-w-0 max-w-full object-contain`}
          />
        </Fragment>
      ))}
    </span>
  );
};
